import { memo } from 'react';

import clsx from 'clsx';
import { Field, FieldArray, FormikContextType, FormikProvider } from 'formik';
import { Award, Plus, X } from 'lucide-react';

import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';

import { SELECT_REWARDS } from '../constants';

import { rewardsOptions } from './constants';

import css from './RewardsFormControl.module.css';

import { useFormSelects } from 'v2/components/form/hooks';
import { REWARD_STUB, UserRewardType } from 'v2/constants';
import { FormReward, WithClassName, WithFormRewards } from 'v2/types';

interface RewardsFormControlProps<T extends WithFormRewards> extends WithClassName {
  formik: FormikContextType<T>;
  initialValue?: FormReward;
  required?: boolean;
  withCount?: boolean;
  excludedTypes?: UserRewardType[];
}

const _RewardsFormControl = <T extends WithFormRewards>({
  className,
  formik,
  initialValue = REWARD_STUB,
  required,
  withCount,
  excludedTypes,
}: RewardsFormControlProps<T>) => {
  const { goldenTicketsOptions, contestTicketsOptions } = useFormSelects();

  const availableRewardsOptions = excludedTypes?.length
    ? rewardsOptions.filter((option) => !excludedTypes.includes(option.value))
    : rewardsOptions;

  const handleTypeChange = (i: number) => () => {
    formik.setFieldValue(`rewards.${i}.value`, '');
  };

  return (
    <div className={clsx(css.rewardsFormControl, className)}>
      <p className={css.rewardsFormControlTitle}>Rewards {required && '*'}</p>

      <FormikProvider value={formik}>
        <FieldArray name="rewards">
          {({ push, remove, form }) => (
            <div className="form">
              {form.values.rewards.map((reward: FormReward, i: number) => (
                <div key={i} className={css.rewardCard}>
                  <div className={css.rewardCardTitleContainer}>
                    <p className={css.rewardCardTitle}>
                      <Award width={16} height={16} />
                      {i + 1} Reward
                    </p>

                    <IconButton color="error" onClick={() => remove(i)} disabled={form.values.rewards.length <= 1}>
                      <X width={18} height={18} />
                    </IconButton>
                  </div>

                  <div className={css.rewardCardFields}>
                    <FormControl size="small" required fullWidth>
                      <InputLabel id={i.toString()}>Type</InputLabel>
                      <Field as={Select} name={`rewards.${i}.type`} labelId={i.toString()} label="Type">
                        {availableRewardsOptions.map((option) => (
                          <MenuItem key={option.id} value={option.value} onClick={handleTypeChange(i)}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>

                    {!SELECT_REWARDS.includes(reward.type) && (
                      <Field as={TextField} name={`rewards.${i}.value`} label="Value" size="small" fullWidth required />
                    )}

                    {reward.type === UserRewardType.CONTEST_TICKET && (
                      <FormControl size="small" required fullWidth>
                        <InputLabel id={i.toString()}>Value</InputLabel>
                        <Field as={Select} name={`rewards.${i}.value`} labelId={i.toString()} label="Value">
                          {contestTicketsOptions.map((t) => (
                            <MenuItem key={t.id} value={t.value}>
                              {t.label}
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    )}

                    {reward.type === UserRewardType.GOLDEN_TICKET && (
                      <FormControl size="small" required fullWidth>
                        <InputLabel id={i.toString()}>Value</InputLabel>
                        <Field as={Select} name={`rewards.${i}.value`} labelId={i.toString()} label="Value">
                          {goldenTicketsOptions.map((t) => (
                            <MenuItem key={t.id} value={t.value}>
                              {t.label}
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    )}

                    {withCount && (
                      <Field
                        as={TextField}
                        name={`rewards.${i}.count`}
                        label="Count"
                        type="number"
                        size="small"
                        fullWidth
                        required
                      />
                    )}
                  </div>
                </div>
              ))}

              <Button variant="outlined" onClick={() => push(initialValue)} fullWidth>
                <Plus width={18} height={18} /> Reward
              </Button>
            </div>
          )}
        </FieldArray>
      </FormikProvider>
    </div>
  );
};

export const RewardsFormControl = memo(_RewardsFormControl) as typeof _RewardsFormControl;
